import React, { useRef } from "react";
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
} from "@mui/material";
import PrintIcon from "@mui/icons-material/Print";
import { useReactToPrint } from "react-to-print";
import config from "../config/Config";
import { formatDate, getAppliedJobDetails } from "../helper/My_Helper";

const ESICForm1 = ({ candidateData = {} }) => {
  const formRef = useRef();
  const appliedJob = getAppliedJobDetails(candidateData);
  const doj= candidateData?.annexure_eleven_form_data?.candidate_doj || appliedJob?.onboard_date ;
  const handlePrint = useReactToPrint({
    contentRef: formRef,
    pageStyle: `
      @page {
        size: A4;
        margin: 8mm 10mm;
      }
      @media print {
        html, body {
          width: 210mm;
          height: 297mm;
          margin: 0;
          padding: 0;
        }
        .no-print {
          display: none !important;
        }
        * {
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
      }
    `,
  });

  // Helper functions for dynamic data
  const getCandidateName = () => {
    const fullName = candidateData?.applicant_form_data?.full_name;
    if (typeof fullName === "object") {
      return [fullName?.first_name, fullName?.middle_name, fullName?.surname]
        .filter(Boolean)
        .join(" ");
    }
    return candidateData?.name || candidateData?.applicant_form_data?.candidate_name || "SAMIKSHA SHARMA";
  };

  const getFatherHusbandName = () => {
    return candidateData?.applicant_form_data?.father_hushband_name || "MP Sharma";
  };

  const getGender = () => {
    const gender = candidateData?.applicant_form_data?.gender || "";
    if (gender === "male") return "पुरुष M";
    if (gender === "female") return "महिला F";
    return "";
  };

  const getMaritalStatus = () => {
    const status = candidateData?.applicant_form_data?.marital_status || "";
    const statusMap = {
      "married": "विवाहित / Married",
      "unmarried": "अविवाहित / Unmarried",
      "widow": "विधवा / Widow",
      "widower": "विधुर / Widower",
      "divorced": "तलाकशुदा / Divorced"
    };
    return statusMap[status] || "";
  };

  const getDOBParts = () => {
    const dob = candidateData?.applicant_form_data?.dob;
    if (dob) {
      return {
        day: dob.date || "9",
        month: dob.month || "6",
        year: dob.year || "1990"
      };
    }
    return { day: "9", month: "6", year: "1990" };
  };

  const getCommunicationAddress = () => {
    const address = candidateData?.applicant_form_data?.communication_address;
    if (address) {
      return {
        address: address.address || "535, A2, Swami Vivekananda enclave, shalimar garden, Shabibad, Ghaziabad",
        pincode: address.pincode || "110049",
        telephone: address.telephone || address.mobile_no || "09311147367",
        email: address.email_id || ""
      };
    }
    return {
      address: "535, A2, Swami Vivekananda enclave, shalimar garden, Shabibad, Ghaziabad",
      pincode: "110049",
      telephone: "09311147367",
      email: ""
    };
  };

  const getPermanentAddress = () => {
    const address = candidateData?.applicant_form_data?.permanent_address;
    if (address) {
      return {
        address: address.address || "161/L, GAUTAM NAGARH 4TH FLOOR",
        pincode: address.pincode || "201005",
        telephone: address.telephone || address.mobile_no || "09311147367",
        email: address.email_id || ""
      };
    }
    return {
      address: "161/L, GAUTAM NAGARH 4TH FLOOR",
      pincode: "201005",
      telephone: "09311147367",
      email: ""
    };
  };

  const getEmployerDetails = () => {
    return {
      name: candidateData?.applicant_form_data?.full_name?.first_name && candidateData?.applicant_form_data?.full_name?.surname
        ? `${candidateData?.applicant_form_data?.full_name?.first_name} ${candidateData?.applicant_form_data?.full_name?.surname}`
        : "",
      address: candidateData?.applicant_form_data?.communication_address?.address || ""
    };
  };

  const getDateOfAppointment = () => {
    const doj = candidateData?.annexure_eleven_form_data?.candidate_doj || appliedJob?.onboard_date;
    if (doj) {
      const date = new Date(doj);
      return {
        day: String(date.getDate()).padStart(2, "0"),
        month: String(date.getMonth() + 1).padStart(2, "0"),
        year: date.getFullYear()
      };
    }
    return { day: "01", month: "1", year: "2026" };
  };

  const getPreviousEmployment = () => {
    const prevOrg = candidateData?.annexure_eleven_form_data?.previous_organization_details;
    if (prevOrg) {
      return {
        previousInsNo: prevOrg.previous_pf || "N/A",
        previous_pf: prevOrg.previous_pf || "N/A",
        previous_designation: prevOrg.previous_designation || "N/A",
        previous_reporting_mng_email: prevOrg.previous_reporting_mng_email || "N/A",
        previous_reporting_mng_mobile_no: prevOrg.previous_reporting_mng_mobile_no || "N/A",
        employerCode: prevOrg.previous_org_code || "N/A",
        employerName: prevOrg.previous_org_name || "N/A",
      };
    }
    return {
      previousInsNo: "AIPPB7608A",
      employerCode: "",
      employerName: "EarlyFit",
    };
  };

  // Get ESIC family members from candidateData
  const getESICFamilyMembers = () => {
    // Check if esic_family_members exists in candidateData
    if (candidateData?.esic_family_members && candidateData.esic_family_members.length > 0) {
      return candidateData.esic_family_members;
    }
    
    // Fallback to applied_jobs
    if (appliedJob?.esic_family_members && appliedJob.esic_family_members.length > 0) {
      return appliedJob.esic_family_members;
    }
    
    // Fallback to applicant_form_data family_members
    const familyMembers = candidateData?.applicant_form_data?.family_members || [];
    if (familyMembers.length > 0) {
      return familyMembers.map(member => ({
        sr_no: member.sr_no || "",
        member_name: member.name || "",
        dob_age: member.age || "",
        relationship: member.particulars || "",
        place_of_residence: member.is_dependent === "yes" ? "Residing with" : "Not residing",
        residing_with: member.is_dependent === "yes" ? "Yes" : "No"
      }));
    }
    
    // Default fallback data
    return [
      { sr_no: "1", member_name: "MP Sharma", dob_age: "70", relationship: "Father", place_of_residence: "Not residing", residing_with: "No" },
      { sr_no: "2", member_name: "", dob_age: "", relationship: "", place_of_residence: "", residing_with: "" },
      { sr_no: "3", member_name: "", dob_age: "", relationship: "", place_of_residence: "", residing_with: "" },
      { sr_no: "4", member_name: "", dob_age: "", relationship: "", place_of_residence: "", residing_with: "" },
      { sr_no: "5", member_name: "", dob_age: "", relationship: "", place_of_residence: "", residing_with: "" },
    ];
  };

  const getDateOfValidity = () => {
    const doj = candidateData?.annexure_eleven_form_data?.candidate_doj || appliedJob?.onboard_date;
    if (doj) {
      const date = new Date(doj);
      date.setMonth(date.getMonth() + 3);
      return `${String(date.getDate()).padStart(2, "0")}/${String(date.getMonth() + 1).padStart(2, "0")}/${date.getFullYear()}`;
    }
    return "22/10/2026";
  };

  const cellStyle = {
    border: "1px solid #000",
    fontSize: "10px",
    p: 0.4,
    verticalAlign: "top",
  };

  const headerCellStyle = {
    ...cellStyle,
    fontWeight: "bold",
    textAlign: "center",
    backgroundColor: "#f0f0f0",
  };

  const dobParts = getDOBParts();
  const commAddress = getCommunicationAddress();
  const permAddress = getPermanentAddress();
  const employer = getEmployerDetails();
  const dateOfAppointment = getDateOfAppointment();
  const prevEmployment = getPreviousEmployment();
  const esicFamilyMembers = getESICFamilyMembers();
  const empCode = candidateData?.annexure_eleven_form_data?.employee_number || appliedJob?.id_card_details?.employee_code || "EMP123456";
  const branchoffice = candidateData?.annexure_eleven_form_data?.place_of_posting || "N/A";

  return (
    <Box>
      {/* Print Button */}
      <Box sx={{ textAlign: "center", mb: 2 }} className="no-print">
        <Button
          variant="contained"
          startIcon={<PrintIcon />}
          onClick={handlePrint}
        >
          Print
        </Button>
      </Box>

      <Box
        ref={formRef}
        sx={{
          backgroundColor: "#fff",
          p: 1.5,
          fontFamily: '"Times New Roman", Times, serif',
          maxWidth: "210mm",
          mx: "auto",
          fontSize: "11px",
          lineHeight: 1.3,
          color: "#000",
          border: "1px solid #999",
        }}
      >
        {/* Header */}
        <Box sx={{ position: "relative", mb: 1, minHeight: "42px" }}>
          <Box sx={{ position: "absolute", left: 0, top: 0 }}>
            <img
              src={
                config?.IMAGE_PATH
                  ? config.IMAGE_PATH + "logo512.png"
                  : "/logo512.png"
              }
              alt="Logo"
              height="40"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = "/logo512.png";
              }}
            />
          </Box>

          <Typography
            align="center"
            sx={{ fontSize: "14px", fontWeight: "bold", pt: 0.2 }}
          >
            घोषणा पत्र DECLARATION FORM
          </Typography>
          <Typography align="center" sx={{ fontSize: "11px" }}>
            फार्म-1 / Form-1
          </Typography>
          <Typography
            align="center"
            sx={{ fontSize: "9.5px", mt: 0.3, px: 4 }}
          >
            To be filled by employee after reading instruction overleaf. Two
            Postcard Size photographs to be attached with the form. This form
            is free of cost.
          </Typography>
        </Box>

        {/* Section A & B */}
        <Grid container spacing={1} sx={{ mb: 1 }}>
          {/* (A) INSURED PERSON'S PARTICULARS */}
          <Grid item xs={6}>
            <Box
              sx={{
                border: "1px solid #000",
                p: 0.8,
                height: "100%",
              }}
            >
              <Typography
                sx={{
                  fontSize: "11px",
                  fontWeight: "bold",
                  mb: 0.8,
                  textAlign: "center",
                  borderBottom: "1px solid #000",
                  pb: 0.3,
                }}
              >
                (A) बीमाकृत व्यक्ति के विवरण / INSURED PERSON'S PARTICULARS
              </Typography>

              <Typography sx={{ fontSize: "10px", mb: 0.4 }}>
                1. बीमा संख्या / Insurance No. _______________________________
              </Typography>

              <Typography sx={{ fontSize: "10px", mb: 0.4 }}>
                2. नाम (स्पष्ट अक्षरों में) / Name in block letters
              </Typography>
              <Box
                sx={{
                  borderBottom: "1px solid #000",
                  minHeight: "18px",
                  mb: 0.6,
                  px: 0.5,
                }}
              >
                <Typography sx={{ fontSize: "10px", fontWeight: "bold" }}>
                  {getCandidateName().toUpperCase()}
                </Typography>
              </Box>

              <Typography sx={{ fontSize: "10px", mb: 0.4 }}>
                3. पिता/पति का नाम / Father's/Husband's Name
              </Typography>
              <Box
                sx={{
                  borderBottom: "1px solid #000",
                  minHeight: "18px",
                  mb: 0.6,
                  px: 0.5,
                }}
              >
                <Typography sx={{ fontSize: "10px" }}>
                  {getFatherHusbandName()}
                </Typography>
              </Box>

              <Grid container spacing={0.5} sx={{ mb: 0.5 }}>
                <Grid item xs={6}>
                  <Typography sx={{ fontSize: "10px" }}>
                    4. जन्म की तिथि / Date of Birth
                  </Typography>
                  <Box sx={{ display: "flex", gap: 0.5, mt: 0.3 }}>
                    <Box sx={{ border: "1px solid #000", width: "28px", height: "18px", textAlign: "center", fontSize: "9px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      {dobParts.day}
                    </Box>
                    <Box sx={{ border: "1px solid #000", width: "28px", height: "18px", textAlign: "center", fontSize: "9px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      {dobParts.month}
                    </Box>
                    <Box sx={{ border: "1px solid #000", width: "36px", height: "18px", textAlign: "center", fontSize: "9px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      {dobParts.year}
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Typography sx={{ fontSize: "10px" }}>
                    5. वैवाहिक स्थिति / Marital Status
                  </Typography>
                  <Box sx={{ borderBottom: "1px solid #000", minHeight: "18px", mt: 0.3, px: 0.5 }}>
                    <Typography sx={{ fontSize: "9.5px" }}>
                      {getMaritalStatus()}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>

              <Typography sx={{ fontSize: "10px", mb: 0.5 }}>
                6. लिंग / Sex : {getGender()}
              </Typography>

              <Typography sx={{ fontSize: "10px", mb: 0.3 }}>
                7. वर्तमान पता / Present Address
              </Typography>
              <Box
                sx={{
                  border: "1px solid #000",
                  minHeight: "38px",
                  mb: 0.5,
                  p: 0.3,
                }}
              >
                <Typography sx={{ fontSize: "9.5px" }}>
                  {commAddress.address}
                </Typography>
              </Box>
              <Typography sx={{ fontSize: "9.5px" }}>
                पिन कोड / Pin Code {commAddress.pincode} टेलीफोन / ई-मेल {commAddress.telephone}
              </Typography>

              <Typography sx={{ fontSize: "10px", mt: 0.6, mb: 0.3 }}>
                8. स्थायी पता / Permanent Address
              </Typography>
              <Box
                sx={{
                  border: "1px solid #000",
                  minHeight: "38px",
                  mb: 0.5,
                  p: 0.3,
                }}
              >
                <Typography sx={{ fontSize: "9.5px" }}>
                  {permAddress.address}
                </Typography>
              </Box>
              <Typography sx={{ fontSize: "9.5px" }}>
                पिन कोड / Pin Code {permAddress.pincode} टेलीफोन / ई-मेल {permAddress.telephone}
              </Typography>

              <Typography sx={{ fontSize: "9.5px", mt: 0.5 }}>
                शाखा कार्यालय / Branch Office : {branchoffice}
              </Typography>
              <Typography sx={{ fontSize: "9.5px" }}>
                औषधालय / Dispensary ________________
              </Typography>
            </Box>
          </Grid>

          {/* (B) EMPLOYER'S PARTICULARS */}
          <Grid item xs={6}>
            <Box
              sx={{
                border: "1px solid #000",
                p: 0.8,
                height: "100%",
              }}
            >
              <Typography
                sx={{
                  fontSize: "11px",
                  fontWeight: "bold",
                  mb: 0.8,
                  textAlign: "center",
                  borderBottom: "1px solid #000",
                  pb: 0.3,
                }}
              >
                (B) नियोक्ता के विवरण / EMPLOYER'S PARTICULARS
              </Typography>

              <Typography sx={{ fontSize: "10px", mb: 0.5 }}>
                9. नियोक्ता की कोड संख्या / Employer's Code No.
                {empCode ? ` ${empCode}` : " _______________________________"}
              </Typography>

              <Typography sx={{ fontSize: "10px", mb: 0.3 }}>
                10. नियुक्ति की तारीख / Date of Appointment
              </Typography>
              <Box sx={{ display: "flex", gap: 0.5, mb: 0.8 }}>
                <Box sx={{ border: "1px solid #000", width: "28px", height: "18px", textAlign: "center", fontSize: "9px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  {dateOfAppointment.day}
                </Box>
                <Box sx={{ border: "1px solid #000", width: "28px", height: "18px", textAlign: "center", fontSize: "9px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  {dateOfAppointment.month}
                </Box>
                <Box sx={{ border: "1px solid #000", width: "36px", height: "18px", textAlign: "center", fontSize: "9px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  {dateOfAppointment.year}
                </Box>
              </Box>

              <Typography sx={{ fontSize: "10px", mb: 0.3 }}>
                11. नियोक्ता का नाम और पता / Name & Address of the Employer
              </Typography>
              <Box
                sx={{
                  border: "1px solid #000",
                  minHeight: "50px",
                  mb: 0.8,
                  p: 0.3,
                }}
              >
                <Typography sx={{ fontSize: "9.5px" }}>
                {employer.name},{employer.address}
                </Typography>
              </Box>

              <Typography sx={{ fontSize: "10px", mb: 0.4, fontWeight: "bold" }}>
                12. यदि पहले नियोजन में रह हैं तो कृपया निम्नलिखित भरें / In case of any previous employment please fill up the details as under:
              </Typography>

              <Typography sx={{ fontSize: "10px", mb: 0.3 }}>
                (a) पिछली बीमा संख्या / Previous Ins. No. {prevEmployment.previousInsNo}
              </Typography>
              <Typography sx={{ fontSize: "10px", mb: 0.3 }}>
                (b) नियोक्ता कोड संख्या / Employer's Code No. {prevEmployment.employerCode || "_______________"}
              </Typography>
              <Typography sx={{ fontSize: "10px", mb: 0.3 }}>
                (c) नियोक्ता का नाम व पता / Name & Address of the Employer
              </Typography>
              <Box
                sx={{
                  border: "1px solid #000",
                  minHeight: "35px",
                  mb: 0.5,
                  p: 0.3,
                }}
              >
                <Typography sx={{ fontSize: "9.5px" }}>
                  {prevEmployment.employerName}
                </Typography>
              </Box>
              <Typography sx={{ fontSize: "9.5px" }}>
                टेलीफोन नंबर / ई-मेल: {prevEmployment.previous_reporting_mng_mobile_no} / {prevEmployment.previous_reporting_mng_email}
              </Typography>
            </Box>
          </Grid>
        </Grid>

        {/* Nominee Details */}
        <Box sx={{ border: "1px solid #000", p: 0.6, mb: 0 }}>
          <Typography sx={{ fontSize: "10px", mb: 0.5 }}>
            (C) मृत्यु की स्थिति में नकद हितलाभ के भुगतान के लिए क.रा.बी. अधिनियम, 1948 की धारा 71 / क.रा.बी. (केन्द्रीय) नियम, 1950 के नियम 56(2) के अंतर्गत नामित के ब्यौरे /
            Details of Nominee u/s 71 of ESI Act 1948 / Rule-56(2) of ESI (Central) Rules, 1950 for payment of cash benefit in the event of death.
          </Typography>

          <Grid container spacing={1}>
            <Grid item xs={4}>
              <Typography sx={{ fontSize: "10px" }}>नाम / Name</Typography>
              <Box sx={{ borderBottom: "1px solid #000", height: "18px", px: 0.5 }}>
                <Typography sx={{ fontSize: "9.5px" }}></Typography>
              </Box>
            </Grid>
            <Grid item xs={4}>
              <Typography sx={{ fontSize: "10px" }}>नातेदारी / Relationship</Typography>
              <Box sx={{ borderBottom: "1px solid #000", height: "18px", px: 0.5 }}>
                <Typography sx={{ fontSize: "9.5px" }}></Typography>
              </Box>
            </Grid>
            <Grid item xs={4}>
              <Typography sx={{ fontSize: "10px" }}>पता / Address</Typography>
              <Box sx={{ borderBottom: "1px solid #000", height: "18px", px: 0.5 }}>
                <Typography sx={{ fontSize: "9.5px" }}></Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Declaration */}
        <Typography sx={{ fontSize: "9.5px", mb: 0.8, textAlign: "justify" }}>
          मैं एतद्द्वारा घोषणा करता/करती हूँ कि मेरे द्वारा प्रस्तुत किए गए विवरण मेरी जानकारी और विश्वास के अनुसार सही हैं। मैं अपने परिवार के सदस्यों में हुए परिवर्तन की सूचना 15 दिन के भीतर प्रस्तुत करने का वचन भी देता हूँ/देती हूँ।
          <br />
          I hereby declare that the particulars given by me are correct to the best of my knowledge and belief. I undertake to intimate the corporation any changes in the membership of my family within 15 days of such change.
        </Typography>

        <Grid container spacing={2} sx={{ mb: 1 }}>
          <Grid item xs={4}>
            <Typography sx={{ fontSize: "9.5px" }}>
              नियोक्ता के प्रतिहस्ताक्षर / Counter signature by the employer
            </Typography>
            <Box
              sx={{
                borderBottom: "1px solid #000",
                height: "28px",
                mt: 0.5,
              }}
            />
          </Grid>
          <Grid item xs={4}>
            <Typography sx={{ fontSize: "9.0px" }}>
              बीमाकृत व्यक्ति के हस्ताक्षर / अंगूठा निशान / Signature / T.I. of I.P.
            </Typography>
            <Box
              sx={{
                borderBottom: "1px solid #000",
                height: "28px",
                mt: 0.5,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {candidateData?.applicant_form_data?.signature && (
                <img
                  src={`${config?.IMAGE_PATH || ""}${candidateData.applicant_form_data.signature}`}
                  alt="candidate-signature"
                  style={{ maxHeight: "24px", maxWidth: "100%", objectFit: "contain" }}
                />
              )}
            </Box>
          </Grid>
          <Grid item xs={4}>
            <Typography sx={{ fontSize: "9.5px" }}>
              सील सहित हस्ताक्षर / Signature with seal
            </Typography>
            <Box
              sx={{
                borderBottom: "1px solid #000",
                height: "28px",
                mt: 0.5,
              }}
            />
          </Grid>
        </Grid>

        {/* Family Particulars Table - Dynamic from esic_family_members */}
        <Typography
          sx={{
            fontSize: "10.5px",
            fontWeight: "bold",
            mb: 0.5,
            textAlign: "center",
          }}
        >
          (D) बीमाकृत व्यक्ति के परिवारों के विवरण / Family Particulars of Insured person
        </Typography>

        <TableContainer sx={{ border: "1px solid #000", mb: 1 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...headerCellStyle, width: "6%" }}>
                  क्रम / Sl. No.
                </TableCell>
                <TableCell sx={{ ...headerCellStyle, width: "22%" }}>
                  नाम / Name
                </TableCell>
                <TableCell sx={{ ...headerCellStyle, width: "16%" }}>
                  फार्म भरने की तारीख को आयु / जन्म तिथि / Date of Birth / Age as on date of filling form
                </TableCell>
                <TableCell sx={{ ...headerCellStyle, width: "18%" }}>
                  कर्मचारी के साथ नातेदारी / Relationship with the Employee
                </TableCell>
                <TableCell sx={{ ...headerCellStyle, width: "16%" }}>
                  क्या उनके साथ रह रहे हैं? / Whether residing with him/her
                  <br />
                  हाँ Yes / नहीं No
                </TableCell>
                <TableCell sx={{ ...headerCellStyle, width: "22%" }}>
                  यदि नहीं तो आवास का स्थान दर्शाएं / If No, state Place of Residence
                  <br />
                  कस्बा Town / राज्य State
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {esicFamilyMembers.map((member, index) => (
                <TableRow key={member._id || member.id || index}>
                  <TableCell sx={{ ...cellStyle, textAlign: "center", height: "22px" }}>
                    {member.sr_no || index + 1}
                  </TableCell>
                  <TableCell sx={{ ...cellStyle, height: "22px" }}>
                    <Typography sx={{ fontSize: "9.5px" }}>
                      {member.member_name || member.name || ""}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ ...cellStyle, height: "22px" }}>
                    <Typography sx={{ fontSize: "9.5px" }}>
                      {member.dob_age || member.age || member.date_of_birth || ""}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ ...cellStyle, height: "22px" }}>
                    <Typography sx={{ fontSize: "9.5px" }}>
                      {member.relationship || ""}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ ...cellStyle, height: "22px" }}>
                    <Typography sx={{ fontSize: "9.5px" }}>
                      {member.residing_with === "Yes" || member.residing_with === "yes" ? "Yes" : 
                       member.residing_with === "No" || member.residing_with === "no" ? "No" :
                       member.residing_with || ""}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ ...cellStyle, height: "22px" }}>
                    <Typography sx={{ fontSize: "9.5px" }}>
                      {member.place_of_residence || ""}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Temporary Identity Card Section */}
        <Box sx={{ border: "1px solid #000", p: 0.6 }}>
          <Typography
            sx={{
              fontSize: "10.5px",
              fontWeight: "bold",
              mb: 0.6,
              textAlign: "center",
            }}
          >
            क.रा.बी. निगम अस्थायी पहचान पत्र / ESI Corporation Temporary Identity Card
          </Typography>
          <Typography
            sx={{ fontSize: "9px", mb: 0.6, textAlign: "center" }}
          >
            (नियुक्ति की तारीख से 3 महीने तक वैध) / (Valid for 3 month from the date of appointment)
          </Typography>

          <Grid container spacing={1}>
            <Grid item xs={8}>
              <Typography sx={{ fontSize: "10px", mb: 0.3 }}>
                नाम / Name <Box component="span" sx={{ borderBottom: "1px solid #000", px: 1 }}>{getCandidateName()}</Box>
              </Typography>
              <Typography sx={{ fontSize: "10px", mb: 0.3 }}>
                बीमा संख्या / Ins. No. ________________  नियुक्ति की तारीख / Date of appointment {dateOfAppointment.day}/{dateOfAppointment.month}/{dateOfAppointment.year}
              </Typography>
              <Typography sx={{ fontSize: "10px", mb: 0.3 }}>
                शाखा कार्यालय / Branch Office {branchoffice}  औषधालय / Dispensary __________
              </Typography>
              <Typography sx={{ fontSize: "10px", mb: 0.3 }}>
                नियोक्ता की कोड संख्या व पता / Employer's Code No. & Address
              </Typography>
              <Box
                sx={{
                  border: "1px solid #000",
                  minHeight: "28px",
                  mb: 0.5,
                  p: 0.3,
                }}
              >
                <Typography sx={{ fontSize: "9.5px" }}>
                  {empCode} & {employer.address}
                </Typography>
              </Box>

              <Grid container spacing={1} sx={{ mt: 0.5 }}>
                <Grid item xs={4}>
                  <Typography sx={{ fontSize: "9.5px" }}>वैधता / Validity</Typography>
                  <Box sx={{ borderBottom: "1px solid #000", height: "18px", paddingBottom: "2px", px: 0.5 }}>
                    <Typography sx={{ fontSize: "9px" }}>{getDateOfValidity()}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={4}>
                  <Typography sx={{ fontSize: "9.5px" }}>तारीख / Dated</Typography>
                  <Box sx={{ borderBottom: "1px solid #000", height: "18px", paddingBottom: "2px", px: 0.5 }}>
                    <Typography sx={{ fontSize: "9px" }}>{formatDate(doj)}</Typography>
                  </Box>
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={4}>
              <Box
                sx={{
                  border: "1px solid #000",
                  height: "90px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexDirection: "column",
                }}
              >
                {candidateData?.profile_details?.photo || candidateData?.photo ? (
                  <img
                    src={`${config?.IMAGE_PATH || ""}${candidateData.profile_details?.photo || candidateData.photo}`}
                    alt="candidate"
                    style={{ maxHeight: "85px", maxWidth: "100%", objectFit: "contain" }}
                  />
                ) : (
                  <Typography sx={{ fontSize: "9.5px", textAlign: "center" }}>
                    फोटो के लिए स्थान
                    <br />
                    (Space for photograph)
                  </Typography>
                )}
              </Box>
            </Grid>
          </Grid>

          <Grid container spacing={1}>
            <Grid item xs={6}>
              <Typography sx={{ fontSize: "9.5px" }}>
                बीमाकृत व्यक्ति के हस्ताक्षर / अंगूठा निशान
                <br />
                Signature / T.I. of I.P.
              </Typography>
              <Box
                sx={{
                  borderBottom: "1px solid #000",
                  height: "25px",
                  mt: 0.5,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {candidateData?.applicant_form_data?.signature && (
                  <img
                    src={`${config?.IMAGE_PATH || ""}${candidateData.applicant_form_data.signature}`}
                    alt="candidate-signature"
                    style={{ maxHeight: "22px", maxWidth: "100%", objectFit: "contain" }}
                  />
                )}
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Typography sx={{ fontSize: "9.5px" }}>
                सील सहित शाखा प्रबंधक के हस्ताक्षर
                <br />
                Signature of B.M. with seal
              </Typography>
              <Box
                sx={{
                  borderBottom: "1px solid #000",
                  height: "25px",
                  mt: 0.5,
                }}
              />
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Box>
  );
};

export default ESICForm1;